import {
  Form,
  MonetaryInputField,
  SelectField,
  SubmitButton,
  TextAreaInputField,
} from '@/components';
import { useBudgetTypes } from '@/features/master-table/hooks';
import { convertBudgetTypeNameToOrderingKey } from '@/features/project/_utils/budget-and-execution';
import type { ExpenditureCartsModel } from '@/features/project/projects/_models/expenditure-cart';
import { UpsertPriorReserveBudgetItemFormSchema } from '@/features/project/projects/_models/expenditure-item';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof UpsertPriorReserveBudgetItemFormSchema>;

type Props = {
  fiscalYear: number;
  expenditureCarts: z.infer<typeof ExpenditureCartsModel>;
  onSubmit: (values: FormValues) => void;
};

export const UpsertPriorReserveBudgetItemForm: FC<Props> = ({
  fiscalYear,
  expenditureCarts,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    watch,
    setValue,
    resetField,
  } = useForm<FormValues>({
    resolver: zodResolver(UpsertPriorReserveBudgetItemFormSchema),
  });
  const expenditureCartId = watch('expenditureCartId');
  const budgetTypeId = watch('budgetTypeId');
  const budgetTypeOptions = useBudgetTypes({
    params: { fiscalYear: fiscalYear.toString() },
    select: (data) =>
      data
        .flatMap((d) =>
          d.type === 'reserve' ? { value: d.id, label: d.name } : [],
        )
        .sort(
          (a, b) =>
            convertBudgetTypeNameToOrderingKey(a.label) -
            convertBudgetTypeNameToOrderingKey(b.label),
        ),
  });

  useEffect(() => {
    // NOTES: subProject should exist when this component is called.
    const expenditureCart = expenditureCarts.find(
      (cart) => cart.id === expenditureCartId,
    );
    // NOTES: expenditure item that type is reserve should exist zero or one.
    const expenditureItem = expenditureCart?.expenditureItems.find(
      (item) => item.budgetTypeId === budgetTypeId,
    );
    if (expenditureItem) {
      setValue('finalizedAmount', expenditureItem.finalizedAmount ?? 0);
      setValue('remark', expenditureItem.remark);
    } else {
      resetField('finalizedAmount', { defaultValue: null as any });
      resetField('remark', { defaultValue: null as any });
    }
  }, [budgetTypeId, expenditureCartId, expenditureCarts, resetField, setValue]);

  const expenditureCartOptions = expenditureCarts.map((cart) => ({
    value: cart.id,
    label: cart.accountTitle
      ? `${cart.accountingType.name} - ${cart.accountTitle.name}`
      : cart.accountingType.name,
  }));

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="expenditureCartId"
        label={t('expenditure-cart')}
        options={expenditureCartOptions}
        control={control}
        errors={errors}
        disabled={!expenditureCartOptions}
        required
      />
      <SelectField
        name="budgetTypeId"
        label={t('reserve-budget')}
        options={budgetTypeOptions.data}
        control={control}
        errors={errors}
        disabled={!expenditureCartOptions}
        required
      />
      <MonetaryInputField
        name="finalizedAmount"
        label={t('amount')}
        hint={t('hint-prior-reserve-budget-amount')}
        control={control}
        errors={errors}
        unit={unit}
        required
      />
      <TextAreaInputField
        name="remark"
        label={t('expenditure-item-remark')}
        hint={t('hint-upsert-expenditure-item-remark')}
        control={control}
        errors={errors}
        rows={3}
      />
      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
